/**
 * Reglas del service worker en funciones que reciben sus dependencias, para poder probarlas sin
 * levantar uno. sw.ts las conecta a sus eventos; la página usa los nombres de caché
 * (esCacheDelServiceWorker) para limpiar el SW de desarrollo en registrarServiceWorker.ts.
 */

/** Cachés de runtime que usa sw.ts. */
export const NOMBRES_CACHE = {
  imagenes: 'anfora-images-cache',
  estaticos: 'anfora-static-cache',
  fuentes: 'anfora-fonts-cache',
  navegacion: 'anfora-navigation-cache',
} as const;

/**
 * Marcador (caché vacía) del protocolo de activación entre la página y el SW.
 *
 * - Protocolo 1 (versiones anteriores): el SW se activaba solo al instalarse (skipWaiting).
 * - Protocolo 2 (este): el SW nuevo espera hasta que la página manda SKIP_WAITING al cambiar de
 *   pantalla (registrarServiceWorker.ts).
 *
 * Cada SW de protocolo 2 crea el marcador al activarse. Si al instalarse una versión nueva no
 * existe, el SW activo es anterior: sus pestañas no saben pedir la activación, así que la versión
 * nueva se activa sola esa única vez (si no, esperaría hasta que se cerraran todas).
 *
 * Subir el número solo si cambia el contrato de forma que las páginas de la versión anterior ya no
 * puedan activar al SW nuevo; al subirlo, agregar el nombre viejo a CACHES_OBSOLETAS.
 *
 * Limitación conocida: si se regresa (rollback) a una versión de protocolo 1 y luego se vuelve a
 * publicar esta, el marcador sigue ahí; las pestañas con el bundle del rollback no saben pedir la
 * activación y se quedan en la versión vieja hasta que se cierran.
 */
export const MARCADOR_PROTOCOLO = 'anfora-sw-protocolo-2';

/**
 * Cachés de runtime que llenaban versiones anteriores del SW y que ya no deben existir.
 * `anfora-api-cache` guardaba respuestas de la API por usuario (sobrevivían al cerrar sesión).
 */
const CACHES_OBSOLETAS: readonly string[] = ['anfora-api-cache'];

const CACHES_DE_LA_APP: readonly string[] = [
  ...Object.values(NOMBRES_CACHE),
  ...CACHES_OBSOLETAS,
  MARCADOR_PROTOCOLO,
];

/** Lo que los manejadores usan de Cache Storage, del SW y de workbox-expiration. */
export interface DependenciasServiceWorker {
  caches: Pick<CacheStorage, 'has' | 'keys' | 'delete' | 'open'>;
  skipWaiting: () => Promise<void>;
  /** Borra los metadatos de expiración (IndexedDB de workbox-expiration) de una caché. */
  borrarMetadatosDeCache: (nombre: string) => Promise<void>;
}

/** De las cachés que hay en el navegador, las que el SW debe borrar al activarse. */
export function cachesObsoletas(nombres: readonly string[]): string[] {
  return nombres.filter((nombre) => CACHES_OBSOLETAS.includes(nombre));
}

/** Cachés que crea el SW (Workbox o la app); las demás del origen no son suyas. */
export function esCacheDelServiceWorker(nombre: string): boolean {
  return nombre.startsWith('workbox-') || CACHES_DE_LA_APP.includes(nombre);
}

/**
 * Mensaje con el que la app pide activar la versión que espera instalada: el que manda
 * workbox-window `messageSkipWaiting()`, `{ type: 'SKIP_WAITING' }`.
 */
export function esMensajeDeActivarVersion(datos: unknown): boolean {
  return (
    typeof datos === 'object' &&
    datos !== null &&
    (datos as { type?: unknown }).type === 'SKIP_WAITING'
  );
}

/**
 * Evento `install`. La versión nueva espera a que la app la pida al cambiar de pantalla, salvo que
 * el SW activo sea anterior a este protocolo (sin marcador): entonces se activa ya, como hacían esas
 * versiones, porque sus pestañas no saben pedirlo.
 */
export async function alInstalarse({
  caches,
  skipWaiting,
}: Pick<DependenciasServiceWorker, 'caches' | 'skipWaiting'>): Promise<void> {
  if (await caches.has(MARCADOR_PROTOCOLO)) return;
  await skipWaiting();
}

/**
 * Evento `activate`: borra las cachés obsoletas (respuestas de API por usuario) y sus metadatos de
 * expiración en IndexedDB, y deja el marcador del protocolo. La precache la administra Workbox
 * (precacheAndRoute y cleanupOutdatedCaches).
 */
export async function alActivarse({
  caches,
  borrarMetadatosDeCache,
}: Pick<DependenciasServiceWorker, 'caches' | 'borrarMetadatosDeCache'>): Promise<void> {
  const nombres = await caches.keys();
  await Promise.all(
    cachesObsoletas(nombres).map((nombre) => caches.delete(nombre))
  );
  await Promise.all(
    CACHES_OBSOLETAS.map(async (nombre) => {
      try {
        await borrarMetadatosDeCache(nombre);
      } catch {
        // Sin IndexedDB o con un error, los metadatos quedan huérfanos y sin efecto: no se impide
        // que la versión nueva se active.
      }
    })
  );
  await caches.open(MARCADOR_PROTOCOLO);
}

/** Evento `message`: la app pide activar la versión que espera (al cambiar de pantalla). */
export function alRecibirMensaje(
  datos: unknown,
  { skipWaiting }: Pick<DependenciasServiceWorker, 'skipWaiting'>
): void {
  if (esMensajeDeActivarVersion(datos)) void skipWaiting();
}

/**
 * Peticiones a las APIs (SSO, Materialidad y buzón). Van siempre a la red: son datos por usuario y
 * no se guardan en el navegador. El buzón se reconoce aunque viva bajo un prefijo (`/sso/api/Buzon/`).
 */
export function esPeticionDeApi(url: URL): boolean {
  return (
    url.pathname.startsWith('/api') ||
    /\/api\/Buzon\//i.test(url.pathname) ||
    url.hostname.includes('grupoteckio.com') ||
    url.hostname.includes('erps.')
  );
}
